import {
  healthSummary,
  loadBalancerMonthly,
  serviceText,
  type HealthSummary,
  type LoadBalancer,
  type LoadBalancerType,
} from "@cloudserver/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { ExternalLink, Plus, Scale, Trash2 } from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteLoadBalancer, useLoadBalancers } from "../api/load-balancers.js";
import { progressOf, useRunningOperations } from "../api/operations.js";
import { useCatalogue, useProject } from "../api/queries.js";
import { useCanWrite } from "../api/users.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage } from "../ui/feedback.js";
import { money, relativeTime } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Alert, Badge, Button } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { failureToast } from "./networks.js";

const HEALTH_TONE: Record<HealthSummary, "ok" | "warn" | "danger" | "neutral"> = {
  healthy: "ok",
  partial: "warn",
  unhealthy: "danger",
  unknown: "neutral",
  none: "neutral",
};

/** A badge for the health of a balancer or of one target. */
export function HealthBadge({ health }: { health: HealthSummary }) {
  const { t } = useTranslation();
  return <Badge tone={HEALTH_TONE[health]}>{t(`loadBalancers.health.${health}`)}</Badge>;
}

/** "€6.99 per month" for a type in a region, "—" when the catalogue has no price. */
export function monthlyText(
  lb: { type: string; region: string },
  types: LoadBalancerType[] | undefined,
  currency: string | undefined,
  locale: string,
): string {
  const amount = types ? loadBalancerMonthly(lb, types) : null;
  return amount === null ? "—" : money(String(amount), currency ?? "EUR", locale);
}

/**
 * The services as "HTTPS 443 → 8443" pairs, comma-separated; a line breaks between two
 * services, never inside one.
 */
function ServicesList({ lb }: { lb: LoadBalancer }) {
  const { t } = useTranslation();
  if (!lb.services.length) {
    return <span className="whitespace-nowrap text-sm">{t("loadBalancers.noServices")}</span>;
  }
  return (
    <span className="text-sm">
      {lb.services.map((s, index) => (
        <Fragment key={`${s.protocol}-${s.listenPort}`}>
          {index > 0 && ", "}
          <span className="whitespace-nowrap">{`${s.protocol.toUpperCase()} ${serviceText(s)}`}</span>
        </Fragment>
      ))}
    </span>
  );
}

export function LoadBalancersPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const project = useProject(projectId);
  const canWrite = useCanWrite(projectId);
  const lbs = useLoadBalancers(projectId);
  const types = useCatalogue(projectId, "load-balancer-types").data;
  const prices = useCatalogue(projectId, "prices").data;
  const running = useRunningOperations(projectId);
  const remove = useDeleteLoadBalancer(projectId);
  const toast = useToast();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<LoadBalancer | null>(null);
  const readOnly = project.data?.connection?.permission === "read";
  const readOnlyReason = t("errors.reasons.read_only_token");

  const createButton = readOnly ? (
    <Button disabled icon={<Plus className="size-4" aria-hidden />}>
      {t("loadBalancers.create")}
    </Button>
  ) : (
    <Link
      to="/projects/$projectId/load-balancers/new"
      params={{ projectId }}
      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-strong"
    >
      <Plus className="size-4" aria-hidden />
      {t("loadBalancers.create")}
    </Link>
  );
  const open = (lb: LoadBalancer) =>
    void navigate({
      to: "/projects/$projectId/load-balancers/$loadBalancerId",
      params: { projectId, loadBalancerId: lb.id },
    });

  return (
    <>
      <PageHeader
        title={t("nav.loadBalancers")}
        intro={t("loadBalancers.intro")}
        help="load-balancers"
        actions={canWrite && lbs.data?.length ? createButton : undefined}
      />
      {readOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {lbs.error ? (
        <ErrorMessage error={lbs.error} />
      ) : (
        <DataTable<LoadBalancer>
          label={t("nav.loadBalancers")}
          rows={lbs.data}
          rowKey={(lb) => lb.id}
          initialSort={{ key: "name", dir: "asc" }}
          progress={(lb) => progressOf(running.data, lb.id)}
          empty={
            <EmptyState
              icon={<Scale className="size-6" aria-hidden />}
              title={t("loadBalancers.empty")}
              action={canWrite ? createButton : undefined}
              help="load-balancers"
            >
              {t("loadBalancers.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("loadBalancers.name"),
              primary: true,
              // Room for «LB11 · Наименьшее число соединений» next to the other columns.
              className: "min-w-52",
              sort: (lb) => lb.name.toLowerCase(),
              cell: (lb) => (
                <span className="flex min-w-0 flex-col">
                  <Link
                    to="/projects/$projectId/load-balancers/$loadBalancerId"
                    params={{ projectId, loadBalancerId: lb.id }}
                    className="font-medium text-accent-strong hover:underline"
                  >
                    {lb.name}
                  </Link>
                  <span className="text-xs text-muted">
                    {lb.type.toUpperCase()} · {t(`loadBalancers.algorithms.${lb.algorithm}`)}
                  </span>
                </span>
              ),
            },
            {
              key: "health",
              header: t("loadBalancers.healthTitle"),
              sort: (lb) => healthSummary(lb),
              cell: (lb) => <HealthBadge health={healthSummary(lb)} />,
            },
            {
              key: "address",
              header: t("loadBalancers.address"),
              cell: (lb) => (
                <span className="flex flex-col font-mono text-xs">
                  {lb.publicInterface && lb.ipv4 ? (
                    <span>{lb.ipv4}</span>
                  ) : (
                    <span className="font-sans text-muted">{t("loadBalancers.privateOnly")}</span>
                  )}
                  {lb.networks.map((n) => (
                    <span key={n.networkRef} className="text-muted">
                      {n.ip} ({n.name})
                    </span>
                  ))}
                </span>
              ),
            },
            {
              key: "services",
              header: t("loadBalancers.services"),
              cell: (lb) => <ServicesList lb={lb} />,
            },
            {
              key: "targets",
              header: t("loadBalancers.targets"),
              sort: (lb) => lb.targets.length,
              cell: (lb) => (
                <span className="whitespace-nowrap">
                  {t("loadBalancers.targetsCount", { count: lb.targets.length })}
                </span>
              ),
            },
            {
              key: "region",
              header: t("loadBalancers.location"),
              sort: (lb) => lb.region,
              cell: (lb) => (
                <span className="text-muted" title={lb.zone}>
                  {lb.region}
                </span>
              ),
            },
            {
              key: "price",
              header: (
                <span className="inline-flex items-center gap-1">
                  {t("loadBalancers.price")}
                  <FieldHint text={t("loadBalancers.billing")} topic="load-balancers" />
                </span>
              ),
              align: "right",
              sort: (lb) => (types ? (loadBalancerMonthly(lb, types) ?? 0) : 0),
              cell: (lb) => (
                <span className="whitespace-nowrap">
                  {monthlyText(lb, types, prices?.currency, i18n.language)}
                </span>
              ),
            },
            {
              key: "created",
              header: t("loadBalancers.createdAt"),
              hideOnPhone: true,
              sort: (lb) => lb.createdAt,
              cell: (lb) => (
                <span className="text-muted">{relativeTime(lb.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={(lb) => [
            [
              {
                label: t("loadBalancers.open"),
                icon: <ExternalLink className="size-4" aria-hidden />,
                onSelect: () => open(lb),
              },
            ],
            [
              {
                label: t("loadBalancers.delete"),
                icon: <Trash2 className="size-4" aria-hidden />,
                danger: true,
                disabled: readOnly || lb.protection.delete,
                reason: readOnly ? readOnlyReason : t("loadBalancers.why.protected"),
                onSelect: () => setDeleting(lb),
              },
            ],
          ]}
        />
      )}
      <DeleteLoadBalancerDialog
        projectId={projectId}
        lb={deleting}
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={(lb) =>
          remove.mutate(lb.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("loadBalancers.deleted", { name: lb.name }) });
              setDeleting(null);
            },
            onError: (error) => toast(failureToast(t, error)),
          })
        }
      />
    </>
  );
}

/** Delete confirmation: names what goes with the balancer, asks for its name. */
export function DeleteLoadBalancerDialog({
  lb,
  busy,
  onClose,
  onConfirm,
}: {
  projectId: string;
  lb: LoadBalancer | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (lb: LoadBalancer) => void;
}) {
  const { t } = useTranslation();
  return (
    <DangerDialog
      open={lb !== null}
      onOpenChange={(next) => !next && onClose()}
      title={t("loadBalancers.deleteTitle", { name: lb?.name ?? "" })}
      consequences={[
        ...(lb?.ipv4 ? [t("loadBalancers.deleteAddresses", { address: lb.ipv4 })] : []),
        t("loadBalancers.deleteServices"),
        t("loadBalancers.deleteTargets"),
        t("loadBalancers.deleteBilling"),
      ]}
      confirmText={lb?.name ?? ""}
      actionLabel={t("loadBalancers.delete")}
      busy={busy}
      onConfirm={() => lb && onConfirm(lb)}
    />
  );
}
